import { useCallback, useMemo, useState } from 'react';
import {
  signInWithPhoneNumber,
  type ApplicationVerifier,
  type Auth,
  type ConfirmationResult,
} from 'firebase/auth';

export type PhoneOtpBusyStateT = 'idle' | 'sending' | 'verifying';
export type PhoneOtpStageT = 'phone' | 'verify';
export type PhoneOtpErrorStageT = 'send' | 'verify';

export const PHONE_OTP_PHONE_DIGIT_COUNT = 10;
export const PHONE_OTP_CODE_DIGIT_COUNT = 6;

export type UsePhoneOtpOptionsT = Readonly<{
  auth: Auth;
  getRecaptchaVerifier: () => ApplicationVerifier;
  resetRecaptchaVerifier: () => void;
  onError?: (stage: PhoneOtpErrorStageT, error: unknown) => void;
  formatPhoneInput?: (value: string) => string;
  sendErrorMessage?: string;
  verifyErrorMessage?: string;
}>;

export type UsePhoneOtpResultT = Readonly<{
  phone: string;
  code: string;
  confirmation: ConfirmationResult | null;
  stage: PhoneOtpStageT;
  busy: PhoneOtpBusyStateT;
  isBusy: boolean;
  isPhoneComplete: boolean;
  errorMessage: string | null;
  setPhoneInput: (value: string) => void;
  setCodeInput: (value: string) => void;
  sendCode: () => Promise<void>;
  verifyCode: () => Promise<void>;
  reset: () => void;
}>;

export function stripPhoneOtpDigits(raw: string): string {
  return raw.replace(/\D/g, '');
}

export function clampPhoneOtpInput(raw: string): string {
  const digits = stripPhoneOtpDigits(raw);
  const nationalDigits =
    digits.length > PHONE_OTP_PHONE_DIGIT_COUNT && digits.startsWith('1')
      ? digits.slice(1)
      : digits;
  return nationalDigits.slice(0, PHONE_OTP_PHONE_DIGIT_COUNT);
}

export function isPhoneOtpPhoneComplete(raw: string): boolean {
  return stripPhoneOtpDigits(raw).length >= PHONE_OTP_PHONE_DIGIT_COUNT;
}

export function toPhoneOtpDigitSlots(value: string, count: number): string[] {
  const digits = stripPhoneOtpDigits(value).slice(0, count);
  return Array.from({ length: count }, (_, index) => digits[index] ?? '');
}

export function maskPhoneNumber(raw: string): string {
  const digits = stripPhoneOtpDigits(raw);
  if (digits.length <= 3) return digits;
  return `(***) ***-${digits.slice(-4)}`;
}

export function normalizePhoneNumber(raw: string): string {
  let number = raw.replace(/[^\d+]+/g, '');
  number = number.replace(/^00/, '+');
  if (/^1/.test(number)) {
    number = `+${number}`;
  }
  if (!/^\+/.test(number)) {
    number = `+1${number}`;
  }
  return number;
}

export function normalizeOtpCode(raw: string): string {
  return stripPhoneOtpDigits(raw).slice(0, PHONE_OTP_CODE_DIGIT_COUNT);
}

export function usePhoneOtp({
  auth,
  getRecaptchaVerifier,
  resetRecaptchaVerifier,
  onError,
  formatPhoneInput,
  sendErrorMessage = 'Failed to send verification code.',
  verifyErrorMessage = 'Invalid verification code.',
}: UsePhoneOtpOptionsT): UsePhoneOtpResultT {
  const [busy, setBusy] = useState<PhoneOtpBusyStateT>('idle');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isBusy = busy !== 'idle';
  const isPhoneComplete = isPhoneOtpPhoneComplete(phone);
  const stage: PhoneOtpStageT = confirmation ? 'verify' : 'phone';

  const setPhoneInput = useCallback(
    (value: string) => {
      setPhone(formatPhoneInput?.(value) ?? clampPhoneOtpInput(value));
    },
    [formatPhoneInput]
  );

  const setCodeInput = useCallback((value: string) => {
    setCode(normalizeOtpCode(value));
  }, []);

  const reset = useCallback(() => {
    resetRecaptchaVerifier();
    setCode('');
    setConfirmation(null);
    setErrorMessage(null);
    setBusy('idle');
  }, [resetRecaptchaVerifier]);

  const sendCode = useCallback(async () => {
    if (isBusy || confirmation) return;

    setErrorMessage(null);
    setBusy('sending');
    try {
      const verifier = getRecaptchaVerifier();
      const nextConfirmation = await signInWithPhoneNumber(
        auth,
        normalizePhoneNumber(phone),
        verifier
      );
      setConfirmation(nextConfirmation);
    } catch (error: unknown) {
      resetRecaptchaVerifier();
      setErrorMessage(sendErrorMessage);
      onError?.('send', error);
    } finally {
      setBusy('idle');
    }
  }, [
    auth,
    confirmation,
    getRecaptchaVerifier,
    isBusy,
    onError,
    phone,
    resetRecaptchaVerifier,
    sendErrorMessage,
  ]);

  const verifyCode = useCallback(async () => {
    if (isBusy || !confirmation) return;

    setErrorMessage(null);
    setBusy('verifying');
    try {
      await confirmation.confirm(code);
      resetRecaptchaVerifier();
      setCode('');
      setConfirmation(null);
    } catch (error: unknown) {
      setErrorMessage(verifyErrorMessage);
      onError?.('verify', error);
    } finally {
      setBusy('idle');
    }
  }, [code, confirmation, isBusy, onError, resetRecaptchaVerifier, verifyErrorMessage]);

  return useMemo(
    () => ({
      phone,
      code,
      confirmation,
      stage,
      busy,
      isBusy,
      isPhoneComplete,
      errorMessage,
      setPhoneInput,
      setCodeInput,
      sendCode,
      verifyCode,
      reset,
    }),
    [
      phone,
      code,
      confirmation,
      stage,
      busy,
      isBusy,
      isPhoneComplete,
      errorMessage,
      setPhoneInput,
      setCodeInput,
      sendCode,
      verifyCode,
      reset,
    ]
  );
}
